import {
	Button,
	Card,
	Checkbox,
	Col,
	Divider,
	Flex,
	Form,
	FormProps,
	notification,
	Radio,
	RadioChangeEvent,
	Row,
	Select,
	TimePicker,
	Typography,
} from 'antd';
import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useWatch } from 'antd/es/form/Form';
import dayjs from 'dayjs';

//components
import BackButton from 'components/backButton/backButton';
import PageTitle from 'components/title/title';
import ComponentHeader from 'components/componentHeader/componentHeader';
import DatePickerElement from 'components/datePicker/datePicker';
import MapComponent from 'components/map/map';
import FormElement from 'components/formElement/formElement';
import MeetingGuests from '../components/meetingGuests';
import FormListFilesElement from 'components/documentsListForm/documentsListForm';
import CommitteeDecisionElement from '../components/decisions';

// services
import NotificationService from 'core/services/notification';
import { mapResponseErrors } from 'utils/mapResponseErrors';

//models

//styles
import googleMeetLogo from 'assets/icons/google_meet.svg';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { CommitteeDecisionFormItem, CommitteeMeetingGuest, CommitteeMeetingLocation, CreateCommitteeMeetingPayload } from '../models/meetings';
import { Committee } from '../models/committee';
import { onCreateCommitteeMeeting, onGetCommitteeMeetingById } from '../services/meetingServices';
import { onGetCommitteeById } from '../services/committeeServices';
import { isRTL } from 'core/services/language';

const CommitteeMeetingCreatePage: FunctionComponent = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const { id } = useParams();

	const [meetingForm] = Form.useForm();
	const meetingTypeWatcher = useWatch('type', meetingForm);

	const [isLoading, setIsLoading] = useState(true);
	const [guests, setGuests] = useState<CommitteeMeetingGuest[]>([]);
	const [committeeDetails, setCommitteeDetails] = useState<Committee>();

	const meetingTypeWatch = useWatch('type', meetingForm);

	const [meetingLocation, setMeetingLocation] = useState<CommitteeMeetingLocation[]>(['Online']);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const meetingTypeOptions = [
		{
			value: 'Scheduled',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.SCHEDULED')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.FOR_SCHEDULED_MEETINGS')}</p>
				</>
			),
		},
		{
			value: 'Urgent',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.URGENT')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.IMMEDIATE_ACTION_NEEDED')}</p>
				</>
			),
		},
		{
			value: 'Voting',
			label: (
				<>
					<p className="mb-0">{t('ACTIONS.VOTING')}</p>
					<p className="mb-0 text-light">{t('ACTIONS.NO_DELIBERATION_VOTES')}</p>
				</>
			),
		},
	].filter((option) => option.value !== 'Voting' || committeeDetails?.allow_swipe_voting);

	useEffect(() => {
		getCommitteeDetails();
	}, []);

	const getCommitteeDetails = async () => {
		try {
			setIsLoading(true);

			const response = await onGetCommitteeById(id!);
			setCommitteeDetails(response);

			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/boards/committees/meetings');
		}
	};

	const handleTypeChange = (event: RadioChangeEvent) => {
		if (event?.target.value == 'Voting' && meetingForm.getFieldValue('decisions')?.length == 0) {
			meetingForm.setFieldValue('decisions', [{ ar: '', en: '' }]);
		}
	};

	const handleLocationChange = (location: CommitteeMeetingLocation, checked: boolean) => {
		if (checked) {
			setMeetingLocation([...meetingLocation, location]);
			meetingForm.setFieldsValue({ location_type: [...meetingLocation, location] });
		} else {
			const tempLocation = [...meetingLocation];
			tempLocation.splice(tempLocation.indexOf(location), 1);
			setMeetingLocation(tempLocation);
			meetingForm.setFieldsValue({ location_type: tempLocation });
		}
	};

	const onFinish: FormProps<CreateCommitteeMeetingPayload>['onFinish'] = async (values) => {
		try {
			setIsSubmitting(true);

			const payload = new FormData();

			payload.append('type', values.type);
			payload.append('date', dayjs(values.date).format('YYYY-MM-DD hh:mm a'));
			payload.append('location_type', meetingLocation.length == 2 ? 'Both' : meetingLocation[0]);

			values.agendas.forEach((fileObj: { file: any; title: string }, index: number) => {
				payload.append(`agendas[${index}][file]`, fileObj.file);
				payload.append(`agendas[${index}][title]`, fileObj.title);
			});

			values.decisions.forEach((point: CommitteeDecisionFormItem, index: number) => {
				payload.append(`decisions[${index}][ar]`, point.ar ?? '');
				payload.append(`decisions[${index}][en]`, point.en ?? '');
				payload.append(`decisions[${index}][abstention_type]`, point.abstention_type);

				if (point.abstention_type === 'specific_committee_members')
					point.abstaining_committee_member_ids?.forEach((shareholder_id: string, shIndex: number) => {
						payload.append(`decisions[${index}][abstaining_committee_member_ids][${shIndex}]`, shareholder_id);
					});
			});

			if (meetingLocation.includes('Physical')) {
				payload.append('physical_lat', values.physical_lat!);
				payload.append('physical_lng', values.physical_lng!);
				payload.append('physical_address', values.physical_address!);
				payload.append('physical_address_description', values.physical_address_description!);
			}

			if (values?.type === 'Voting' && values?.duration_minutes) {
				const totalMinutes = dayjs(values.duration_minutes).hour() * 60 + dayjs(values.duration_minutes).minute();
				payload.append('duration_minutes', totalMinutes.toString());
			}

			if (values?.type != 'Voting' && guests && guests.length > 0) {
				guests.forEach((guest, index) => {
					payload.append(`guests[${index}][name]`, guest.name);
					payload.append(`guests[${index}][name_en]`, guest.name_en);
					payload.append(`guests[${index}][email]`, guest.email);
					payload.append(`guests[${index}][phone_number]`, guest.phone_number);
					payload.append(`guests[${index}][phone_country_code]`, guest.phone_country_code);
					payload.append(`guests[${index}][position]`, guest.position);
					payload.append(`guests[${index}][organization]`, guest.organization);
					payload.append(`guests[${index}][sector]`, guest.sector);
				});
			}

			await onCreateCommitteeMeeting(id!, payload);

			NotificationService.success(t('COMMON.SUCCESS'), t('ACTIONS.MEETING_CREATED_SUCCESSFULLY'));

			navigate('/dashboard/actions/boards/committees/meetings');
		} catch (error: any) {
			setIsSubmitting(false);
			NotificationService.error(t('COMMON.ERROR'), error?.response?.data?.message || t('COMMON.SUBMIT_ERROR'));
			if (error.status === 422) mapResponseErrors(error, meetingForm);
		}
	};

	return (
		<>
			<PageTitle name={t('PAGES.COMMITTEE_MEETING')} />

			<BackButton />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<ComponentHeader
						title={t('ACTIONS.NEW_NAME_COMMITTEE_MEETING', { name: isRTL() ? committeeDetails?.name.ar : committeeDetails?.name.en })}
						subtitle={t('ACTIONS.MEETING_FILL_OUT_DETAILS')}
					/>

					<Form
						form={meetingForm}
						onFinish={onFinish}
						className="mt-5"
						initialValues={{ type: 'Scheduled', agendas: [{}], decisions: [], voting_duration: dayjs('01:00', 'HH:mm') }}>
						<Row gutter={[16, 16]}>
							<Col span={24} className="mb-3">
								<Flex gap={20}>
									<h6 className="mb-3 fw-medium">{t('ACTIONS.MEETING_TYPE')}:</h6>
									<Form.Item name="type" style={{ maxWidth: '100%' }}>
										<Radio.Group
											style={{ display: 'flex', justifyContent: 'space-between', gap: 20 }}
											options={meetingTypeOptions}
											onChange={handleTypeChange}
										/>
									</Form.Item>
								</Flex>
							</Col>

							<Col span={24} md={12}>
								<DatePickerElement
									name="date"
									rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}
									label={t('ACTIONS.CHOOSE_MEETING_TIME')}
									minDate={
										meetingTypeWatcher == 'Urgent'
											? dayjs().subtract(1, 'day')
											: dayjs().add(committeeDetails?.minimum_notification_days_before_meetings || 1, 'day')
									}
									showTime={{ format: 'hh:mm a' }}
								/>
							</Col>

							<Col span={24} md={12}>
								{meetingTypeWatch === 'Voting' ? (
									<>
										<Typography.Text className="d-block" style={{ marginBottom: 5, display: 'block', fontSize: 15, textAlign: 'start' }}>
											{t('ACTIONS.VOTING_DURATION')}
										</Typography.Text>
										<Form.Item name="duration_minutes" rules={[{ required: true, message: t('COMMON.FIELD_REQUIRED') }]}>
											<TimePicker
												format="HH:mm"
												showNow={false}
												needConfirm={false}
												style={{ width: '100%' }}
												onChange={(time) => meetingForm.setFieldsValue({ duration_minutes: time })}
											/>
										</Form.Item>
									</>
								) : (
									<div>
										<h6 className="mb-3">{t('ACTIONS.LOCATION')}</h6>
										<Flex gap={10} wrap>
											<div>
												<Checkbox
													checked={meetingLocation.includes('Online')}
													className="compoundCheckbox"
													onChange={(e) => handleLocationChange('Online', e.target.checked)}>
													<Flex align="center" gap={5}>
														<p className="fw-medium mb-0">{t('ACTIONS.ONLINE')} - </p> <img src={googleMeetLogo} style={{ height: 25 }} />
													</Flex>
												</Checkbox>
											</div>
											<div>
												<Checkbox className="compoundCheckbox" onChange={(e) => handleLocationChange('Physical', e.target.checked)}>
													<div>
														<p className="fw-medium mb-0">{t('ACTIONS.PHYSICAL')} </p>
													</div>
												</Checkbox>
											</div>
										</Flex>
										<Form.Item
											name="location_type"
											hidden
											noStyle
											initialValue={meetingLocation}
											rules={[{ required: true, message: t('ACTIONS.SELECT_AT_LEAST_ONE_LOCATION') }]}
										/>
									</div>
								)}
							</Col>

							{meetingTypeWatch != 'Voting' && !meetingLocation.includes('Physical') && <Divider />}

							{meetingTypeWatch != 'Voting' && meetingLocation.includes('Physical') && (
								<Col span={24}>
									<div className="my-4">
										<MapComponent
											onSelect={(lat, lng, address) => {
												meetingForm.setFieldsValue({ physical_lat: lat, physical_lng: lng, physical_address: address });
											}}
										/>

										<div className="mt-3">
											<Form.Item name="physical_lat" hidden noStyle rules={[{ required: meetingLocation.includes('Physical') }]} />
											<Form.Item name="physical_lng" hidden noStyle rules={[{ required: meetingLocation.includes('Physical') }]} />

											<FormElement
												fullWidth
												name="physical_address"
												label={t('ACTIONS.ADDRESS')}
												type="text"
												rules={[{ required: meetingLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
											/>

											<FormElement
												fullWidth
												name="physical_address_description"
												label={t('ACTIONS.ADDRESS_DESC')}
												type="text"
												rules={[{ required: meetingLocation.includes('Physical'), message: t('COMMON.FIELD_REQUIRED') }]}
											/>
										</div>
									</div>

									<Divider />
								</Col>
							)}

							{meetingTypeWatch !== 'Voting' && committeeDetails?.allow_external_guest_invitation && (
								<Col span={24}>
									<MeetingGuests onChange={setGuests} guests={guests} />

									<Divider />
								</Col>
							)}

							<Col span={24}>
								<h6 className="mb-3" style={{ fontSize: '1.1rem' }}>
									{t('ACTIONS.MEETING_AGENDA')}
								</h6>

								<FormListFilesElement form={meetingForm} fieldName="agendas" />
							</Col>

							<Divider />

							<Col span={24}>
								<CommitteeDecisionElement type={meetingTypeWatch} form={meetingForm} />
							</Col>

							<Col span={24} className="mt-5">
								<Flex justify="end">
									<Button type="primary" htmlType="submit" loading={isSubmitting}>
										{t('ACTIONS.SCHEDULE')}
									</Button>
								</Flex>
							</Col>
						</Row>
					</Form>
				</Card>
			)}
		</>
	);
};

export default CommitteeMeetingCreatePage;
